import { useState, useEffect, useMemo } from 'react';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Package, Plus, Pencil, Trash2, X, ExternalLink, ChevronDown, ChevronRight, Search, Star, Minus, MoveRight, ShoppingCart, Maximize2, Heart, Check, Info, ChefHat } from 'lucide-react';
import ModalBackdrop from '@/components/ModalBackdrop';
import { useShoppingListStore, type ShoppingListItem } from '@/stores/shopping-list-store';
import { useFavoriteStore, type Favorite } from '@/stores/favorite-store';
import { lockScroll, unlockScroll, forceUnlockAll } from '@/utils/scrollLock';
import { useFamilyStore } from '@/stores/family-store';
import { useInventoryStore, type InventoryCategory, CATEGORY_LABELS } from '@/stores/inventory-store';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';

interface ListFormState {
  name: string;
  description?: string;
  store?: string;
  isPrimary?: boolean;
}

interface ItemFormState {
  name: string;
  category: InventoryCategory | '';
  notes?: string;
  link?: string;
  quantity: number;
  price: string;
  store?: string;
  aisle?: string;
  purchased: boolean;
}

type SortMode = 'name' | 'location';

// Vanliga matvaror för förslag med avdelningar
const COMMON_SHOPPING_ITEMS = [
  { name: 'Konserverade bönor', aisle: 'Konserver' },
  { name: 'Pasta', aisle: 'Torrvaror' },
  { name: 'Ris', aisle: 'Torrvaror' },
  { name: 'Havregröt', aisle: 'Torrvaror' },
  { name: 'Konserverad tomat', aisle: 'Konserver' },
  { name: 'Tonfisk', aisle: 'Konserver' },
  { name: 'Knäckebröd', aisle: 'Bröd & Bageri' },
  { name: 'Nötter', aisle: 'Godis & Snacks' },
  { name: 'Honung', aisle: 'Torrvaror' },
  { name: 'Mjöl', aisle: 'Torrvaror' },
  { name: 'Socker', aisle: 'Torrvaror' },
  { name: 'Salt', aisle: 'Torrvaror' },
  { name: 'Olivolja', aisle: 'Torrvaror' },
  { name: 'Kaffe', aisle: 'Drycker' },
  { name: 'Te', aisle: 'Drycker' },
  { name: 'Bröd', aisle: 'Bröd & Bageri' },
  { name: 'Mjölk', aisle: 'Mejeri' },
  { name: 'Smör', aisle: 'Mejeri' },
  { name: 'Ost', aisle: 'Mejeri' },
  { name: 'Ägg', aisle: 'Mejeri' },
  { name: 'Kött', aisle: 'Kött & Chark' },
  { name: 'Fisk', aisle: 'Fisk & Skaldjur' },
  { name: 'Grönsaker', aisle: 'Frukt & Grönt' },
  { name: 'Frukt', aisle: 'Frukt & Grönt' },
  { name: 'Potatis', aisle: 'Frukt & Grönt' },
  { name: 'Lök', aisle: 'Frukt & Grönt' },
  { name: 'Vitlök', aisle: 'Frukt & Grönt' },
  { name: 'Tomat', aisle: 'Frukt & Grönt' },
  { name: 'Gurka', aisle: 'Frukt & Grönt' },
  { name: 'Paprika', aisle: 'Frukt & Grönt' },
];

// Vanliga avdelningar i en supermarket
const COMMON_AISLES = [
  'Frukt & Grönt',
  'Mejeri',
  'Kött & Chark',
  'Fisk & Skaldjur',
  'Bröd & Bageri',
  'Konserver',
  'Torrvaror',
  'Fryst',
  'Drycker',
  'Godis & Snacks',
  'Kemi & Städning',
  'Hygien',
  'Djurmat',
  'Hushåll',
  'Baby',
];

const CATEGORY_AISLES: Record<string, string[]> = {
  mat: ['Frukt & Grönt', 'Mejeri', 'Kött & Chark', 'Fisk & Skaldjur', 'Bröd & Bageri', 'Konserver', 'Torrvaror', 'Fryst', 'Drycker', 'Godis & Snacks'],
  medicin: ['Apotek', 'Hygien', 'Hälsokost'],
  hygien: ['Hygien', 'Kemi & Städning', 'Baby'],
  verktyg: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  kommunikation: ['Elektronik', 'Hushåll'],
  varme: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  ljus: ['Hushåll', 'Järnhandel'],
  dokument: ['Kontorsmaterial', 'Hushåll'],
  ovrigt: [...COMMON_AISLES],
};

function countLetters(value: string) {
  const matches = value.match(/\p{L}/gu);
  return matches?.length ?? 0;
}

export default function ShoppingListPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuthStore();
  const family = useFamilyStore((s) => s.family);
  const lists = useShoppingListStore(state => state.lists);
  const items = useShoppingListStore(state => state.items);
  const fetchFromServer = useShoppingListStore(state => state.fetchFromServer);
  const createList = useShoppingListStore(state => state.createList);
  const updateList = useShoppingListStore(state => state.updateList);
  const deleteList = useShoppingListStore(state => state.deleteList);
  const createItem = useShoppingListStore(state => state.createItem);
  const updateItem = useShoppingListStore(state => state.updateItem);
  const deleteItem = useShoppingListStore(state => state.deleteItem);
  const togglePurchased = useShoppingListStore(state => state.togglePurchased);
  const clearPurchasedForList = useShoppingListStore(state => state.clearPurchasedForList);

  const favorites = useFavoriteStore(state => state.favorites);
  const fetchFavorites = useFavoriteStore(state => state.fetchFromServer);
  const createFavorite = useFavoriteStore(state => state.createFavorite);
  const updateFavorite = useFavoriteStore(state => state.updateFavorite);
  const deleteFavorite = useFavoriteStore(state => state.deleteFavorite);

  const [searchParams, setSearchParams] = useSearchParams();

  // Premium limits for shopping lists
  const FREE_TIER_LIMIT = 3; // Free tier: 3 lists
  const PREMIUM_LIMIT = 100; // Premium tier: 100 lists
  const FREE_TIER_ITEMS = 30; // Free tier: 30 items per list
  const PREMIUM_ITEMS = 500; // Premium tier: 500 items per list
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;

  const [showListForm, setShowListForm] = useState(false);
  const [editingList, setEditingList] = useState<null | { id: string; name: string; description?: string; store?: string; isPrimary?: boolean }>(null);
  const [listForm, setListForm] = useState<ListFormState>({ name: '', description: '', store: '', isPrimary: false });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('location');

  const [showItemForm, setShowItemForm] = useState(false);
  const [currentListId, setCurrentListId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ShoppingListItem | null>(null);
  const [itemForm, setItemForm] = useState<ItemFormState>({ name: '', category: 'mat', notes: '', link: '', quantity: 1, price: '0', store: '', aisle: '', purchased: false });

  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set());
  const [fullscreenListId, setFullscreenListId] = useState<string | null>(null);
  const [expandedNotesInFullscreen, setExpandedNotesInFullscreen] = useState<Set<string>>(new Set());
  const [confirmDeleteListId, setConfirmDeleteListId] = useState<string | null>(null);
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeature, setPremiumFeature] = useState<string>('');
  const [confirmClearListId, setConfirmClearListId] = useState<string | null>(null);
  const [showMoveItemModal, setShowMoveItemModal] = useState(false);
  const [itemToMove, setItemToMove] = useState<ShoppingListItem | null>(null);
  const [targetListId, setTargetListId] = useState<string>('');
  const [showAddToInventory, setShowAddToInventory] = useState(false);
  const [itemForInventory, setItemForInventory] = useState<ShoppingListItem | null>(null);
  const [inventoryForm, setInventoryForm] = useState({
    name: '',
    category: 'mat' as InventoryCategory,
    quantity: 1,
    unit: 'st',
    expirationDate: '',
    location: 'Hemma',
    notes: '',
    link: '',
  });
  const [showInventoryLocationSuggestions, setShowInventoryLocationSuggestions] = useState(false);
  const [inventoryLocationSearch, setInventoryLocationSearch] = useState('');

  // Favoriter state
  const [showFavorites, setShowFavorites] = useState(false);
  const [showFavoriteForm, setShowFavoriteForm] = useState(false);
  const [editingFavorite, setEditingFavorite] = useState<string | null>(null);
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const [favoriteForm, setFavoriteForm] = useState<{
    name: string;
    category: InventoryCategory | '';
    notes?: string;
    link?: string;
    store?: string;
    aisle?: string;
    price?: string;
  }>({ name: '', category: 'mat', notes: '', link: '', store: '', aisle: '', price: '0' });
  const [confirmDeleteFavoriteId, setConfirmDeleteFavoriteId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState('');

  // URL validation helper
  const validateUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return true; // Empty is valid
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };
  
  // Quick add favorite dropdown state
  const [showQuickAddFavorite, setShowQuickAddFavorite] = useState<string | null>(null);
  const [quickAddSearch, setQuickAddSearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [addedFavoriteId, setAddedFavoriteId] = useState<string | null>(null);

  // Helper to open the item form without causing the page to jump (preserve scroll)
  const openItemForm = () => {
    // Check Premium limits for items in current list
    if (currentListId) {
      const currentListItems = items.filter(i => i.listId === currentListId);
      const itemCount = currentListItems.length;
      
      if (!hasPremiumAccess && itemCount >= FREE_TIER_ITEMS) {
        setPremiumFeature('Fler än 30 artiklar per inköpslista');
        setShowPremiumModal(true);
        return;
      }
      
      if (hasPremiumAccess && itemCount >= PREMIUM_ITEMS) {
        // Silent block for premium users at their limit
        return;
      }
    }
    
    const scrollY = window.scrollY || window.pageYOffset || 0;
    try { lockScroll(); } catch { /* noop */ }
    // Use requestAnimationFrame to allow UI updates to settle, then restore scroll
    setShowItemForm(true);
    requestAnimationFrame(() => {
      try { window.scrollTo({ top: scrollY, behavior: 'auto' }); } catch { window.scrollTo(0, scrollY); }
    });
  };

  // Generic helper to open any modal: acquire scroll lock before showing
  const openModal = (op: () => void) => {
    try { lockScroll(); } catch { /* noop */ }
    op();
  };

  // Ensure scroll is unlocked when no modal is open. This is a safety-net
  // for cases where a modal close path missed `unlockScroll()` due to
  // race conditions — call `unlockScroll()` first, then `forceUnlockAll()`
  // as a final recovery measure if the body remains locked.
  const isAnyModalOpen = showItemForm || showListForm || showSuggestions || showStoreSuggestions ||
    showAisleSuggestions || showMoveItemModal || showAddToInventory || showInventoryLocationSuggestions ||
    confirmDeleteListId !== null || confirmDeleteItemId !== null || confirmClearListId !== null || !!fullscreenListId ||
    showFavorites || showFavoriteForm || confirmDeleteFavoriteId !== null;

  useEffect(() => {
    if (isAnyModalOpen) return;
    // Don't call unlockSync immediately — ModalBackdrop and other modal
    // components handle unlock on unmount. Instead, wait a short period
    // and then force-unlock only if a lock remains. This avoids racing
    // with unmount-side unlocks which can cause the page to restore too
    // early and jump to the top.
    const timer = setTimeout(() => {
      try {
        const cur = parseInt(document.body.dataset.scrollLockCount || '0', 10) || 0;
        if (cur > 0) {
          try { forceUnlockAll(); } catch (_e) { /* noop */ }
        }
      } catch (_e) { /* noop */ }
    }, 250);

    return () => clearTimeout(timer);
  }, [isAnyModalOpen]);
  
  const addInventoryItem = useInventoryStore(state => state.addItem);
  const inventoryItems = useInventoryStore(state => state.items);

  const authHydrated = useAuthHydrated();

  useEffect(() => {
    if (!authHydrated) return;
    const familyId = useFamilyStore.getState().family?.id;
    fetchFromServer(familyId).catch(() => {});
    fetchFavorites(familyId).catch(() => {});
  }, [fetchFromServer, fetchFavorites, authHydrated]);

  const scrollListIntoView = (listId: string, delay = 100) => {
    window.setTimeout(() => {
      const listElement = document.getElementById(`shopping-list-${listId}`);
      if (!listElement) return;

      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
      const topOffset = isMobileViewport ? 64 : 24;
      const y = listElement.getBoundingClientRect().top + window.pageYOffset - topOffset;

      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, delay);
  };

  // Hantera förifylld data från navigation state
  useEffect(() => {
    const navState = location.state as unknown;
    const state = (navState && typeof navState === 'object' ? (navState as { prefilledItem?: { name?: string; category?: InventoryCategory; quantity?: number } }) : null);
    if (state?.prefilledItem) {
      const { name, category, quantity } = state.prefilledItem;
      const primaryList = lists.find(l => l.isPrimary);
      if (primaryList) {
        setCurrentListId(primaryList.id);
        setItemForm({
          name: name || '',
          category: category || 'mat',
          notes: '',
          link: '',
          quantity: quantity || 1,
          price: '0',
          store: '',
          aisle: '',
          purchased: false,
        });
        openItemForm();
        // Rensa location state
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, lists, openItemForm]);

  // Expandera lista automatiskt om listId finns i URL:en
  useEffect(() => {
    const listId = searchParams.get('listId');
    const shouldAddItem = searchParams.get('addItem') === 'true';
    
    if (listId && lists.some(l => l.id === listId)) {
      setExpandedLists(prev => new Set(prev).add(listId));
      
      // Om addItem-parametern finns, öppna item-formuläret
      if (shouldAddItem) {
        setCurrentListId(listId);
        setItemForm({
          name: '',
          category: '',
          notes: '',
          link: '',
          quantity: 1,
          price: '0',
          store: '',
          aisle: '',
          purchased: false,
        });
        openItemForm();
        // Ta bort parametrarna från URL:en
        searchParams.delete('listId');
        searchParams.delete('addItem');
        setSearchParams(searchParams, { replace: true });
      } else {
        // Scrolla till listan efter en längre fördröjning så att DOM:en hinner expandera och rendera
        scrollListIntoView(listId, 300);
        // Ta bort parametern från URL:en efter att vi har expanderat listan
        searchParams.delete('listId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, lists, openItemForm]);

  // Previously: auto-open primary list on load. Behavior changed: keep all lists closed by default.

  const filteredSuggestions = useMemo(() => {
    if (!suggestionSearch) return COMMON_SHOPPING_ITEMS;
    return COMMON_SHOPPING_ITEMS.filter(s => 
      s.name.toLowerCase().includes(suggestionSearch.toLowerCase())
    );
  }, [suggestionSearch]);

  // Hämta alla unika butiker från befintliga artiklar
  const existingStores = useMemo(() => {
    const stores = new Set<string>();
    items.forEach(item => {
      if (item.store) stores.add(item.store);
    });
    return Array.from(stores).sort();
  }, [items]);

  const filteredStores = useMemo(() => {
    if (!storeSuggestionSearch) return existingStores;
    return existingStores.filter(s => 
      s.toLowerCase().includes(storeSuggestionSearch.toLowerCase())
    );
  }, [existingStores, storeSuggestionSearch]);

  const filteredAisles = useMemo(() => {
    const aisles = CATEGORY_AISLES[itemForm.category || ''] || COMMON_AISLES;
    if (!aisleSuggestionSearch) return aisles;
    return aisles.filter(a => 
      a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase())
    );
  }, [aisleSuggestionSearch, itemForm.category]);

  // Hämta unika platser från förrådet
  const existingInventoryLocations = useMemo(() => {
    const locations = new Set<string>();
    inventoryItems.forEach(item => {
      if (item.location) locations.add(item.location);
    });
    locations.add('Hemma');
    return Array.from(locations).sort();
  }, [inventoryItems]);

  const filteredInventoryLocations = useMemo(() => {
    if (!inventoryLocationSearch) return existingInventoryLocations;
    return existingInventoryLocations.filter(loc => 
      loc.toLowerCase().includes(inventoryLocationSearch.toLowerCase())
    );
  }, [existingInventoryLocations, inventoryLocationSearch]);

  const trimmedListName = listForm.name.trim();
  const hasMinimumLettersForNewList = countLetters(trimmedListName) >= 3;
  const isListSaveDisabled = editingList ? !trimmedListName : !trimmedListName || !hasMinimumLettersForNewList;

  // Scroll lock is handled per-modal using `openModal()` and explicit `unlockScroll()` on close.

  // Stäng quick add dropdown när man klickar utanför
  useEffect(() => {
    if (!showQuickAddFavorite) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Kontrollera om klicket är utanför dropdown-elementet
      if (!target.closest('.quick-add-dropdown') && !target.closest('.quick-add-fav-button')) {
        setShowQuickAddFavorite(null);
        setQuickAddSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickAddFavorite]);

  // Beräkna dropdown-position baserat på knappens position
  useEffect(() => {
    if (!showQuickAddFavorite) {
      setDropdownPosition(null);
      return;
    }

    const updatePosition = () => {
      const button = document.querySelector(`.quick-add-fav-button[data-list-id="${showQuickAddFavorite}"]`) as HTMLElement;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    };

    updatePosition();

    // Uppdatera position vid scroll
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showQuickAddFavorite]);

  // Lock scroll when fullscreen modal is open
  useEffect(() => {
    if (!fullscreenListId) {
      // Clear expanded notes when closing fullscreen
      setExpandedNotesInFullscreen(new Set());
      return;
    }

    try { lockScroll(); } catch { /* noop */ }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullscreenListId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      try { unlockScroll(); } catch { /* noop */ }
    };
  }, [fullscreenListId]);

  const handleSetPrimary = async (listId: string) => {
    await updateList(listId, { isPrimary: true });
    // Hämta uppdaterad data från servern för att säkerställa synkning
    await fetchFromServer();
    // Expandera den primära listan och stäng andra
    setExpandedLists(new Set([listId]));
  };

  const toggleList = (listId: string) => {
    setExpandedLists(prev => {
      // Om listan redan är öppen, stäng den
      if (prev.has(listId)) {
        return new Set();
      }
      // Annars öppna endast denna lista (stäng alla andra)
      const newSet = new Set([listId]);

      // Scrolla till listans topp med en liten marginal. Detta ger bättre precision på mobil.
      scrollListIntoView(listId);
      
      return newSet;
    });
  };

  // Favorit-funktioner
  const handleCreateFavorite = async () => {
    if (!favoriteForm.name.trim()) return;
    
    // Validate link
    if (favoriteForm.link && !validateUrl(favoriteForm.link)) {
      setLinkError('Ogiltig URL. Ange en giltig webbadress (t.ex. https://example.com)');
      return;
    }
    
    const familyId = family?.id;
    await createFavorite({
      name: favoriteForm.name.trim(),
      category: favoriteForm.category || 'mat',
      notes: favoriteForm.notes,
      link: favoriteForm.link,
      store: favoriteForm.store,
      aisle: favoriteForm.aisle,
      price: parseFloat(((favoriteForm.price || '0') as string).replace(',', '.')) || 0,
    }, familyId);
    
    setFavoriteForm({ name: '', category: 'mat', notes: '', link: '', store: '', aisle: '', price: '0' });
    setLinkError('');
    setShowFavoriteForm(false);
    try { unlockScroll(); } catch { /* noop */ }
  };

  const handleUpdateFavorite = async () => {
    if (!editingFavorite || !favoriteForm.name.trim()) return;
    
    // Validate link
    if (favoriteForm.link && !validateUrl(favoriteForm.link)) {
      setLinkError('Ogiltig URL. Ange en giltig webbadress (t.ex. https://example.com)');
      return;
    }
    
    const familyId = family?.id;
    await updateFavorite(editingFavorite, {
      name: favoriteForm.name.trim(),
      category: favoriteForm.category || 'mat',
      notes: favoriteForm.notes,
      link: favoriteForm.link,
      store: favoriteForm.store,
      aisle: favoriteForm.aisle,
      price: parseFloat(((favoriteForm.price || '0') as string).replace(',', '.')) || 0,
    }, familyId);
    
    setEditingFavorite(null);
    setFavoriteForm({ name: '', category: 'mat', notes: '', link: '', store: '', aisle: '', price: '0' });
    setLinkError('');
    setShowFavoriteForm(false);
    try { unlockScroll(); } catch { /* noop */ }
  };

  const handleDeleteFavorite = async (favoriteId: string) => {
    const familyId = family?.id;
    await deleteFavorite(favoriteId, familyId);
    setConfirmDeleteFavoriteId(null);
    try { unlockScroll(); } catch { /* noop */ }
  };

  const handleEditFavorite = (favorite: Favorite) => {
    setEditingFavorite(favorite.id);
    setFavoriteForm({
      name: favorite.name,
      category: favorite.category as InventoryCategory || 'mat',
      notes: favorite.notes,
      link: favorite.link,
      store: favorite.store,
      aisle: favorite.aisle,
      price: favorite.price != null && favorite.price > 0 ? (favorite.price % 1 === 0 ? String(favorite.price) : String(favorite.price).replace('.', ',')) : '0',
    });
    openModal(() => setShowFavoriteForm(true));
  };

  // Lägg till favorit direkt i inköpslista
  const handleQuickAddFavorite = async (listId: string, favorite: Favorite) => {
    setCurrentListId(listId);
    
    // Kontrollera om artikeln redan finns i listan
    const existingItem = items.find(item => 
      item.listId === listId && 
      item.name.toLowerCase() === favorite.name.toLowerCase()
    );

    if (existingItem) {
      // Om artikeln redan finns, öka bara antalet
      await updateItem(existingItem.id, { quantity: existingItem.quantity + 1 });
    } else {
      // Annars lägg till ny artikel
      await createItem({
        listId,
        name: favorite.name,
        category: favorite.category as InventoryCategory || 'mat',
        notes: favorite.notes || '',
        link: favorite.link || '',
        quantity: 1,
        price: favorite.price || 0,
        store: favorite.store || '',
        aisle: favorite.aisle || '',
        purchased: false,
      });
    }
    
    // Visa "lagts till" feedback
    setAddedFavoriteId(favorite.id);
    
    // Ta bort feedback efter en kort delay
    setTimeout(() => {
      setAddedFavoriteId(null);
    }, 1500);
  };

  // Filtrera favoriter baserat på sökning
  const filteredFavorites = useMemo(() => {
    if (!quickAddSearch.trim()) return favorites;
    const search = quickAddSearch.toLowerCase();
    return favorites.filter(fav => 
      fav.name.toLowerCase().includes(search) ||
      (fav.notes && fav.notes.toLowerCase().includes(search)) ||
      (fav.store && fav.store.toLowerCase().includes(search)) ||
      (fav.aisle && fav.aisle.toLowerCase().includes(search))
    );
  }, [favorites, quickAddSearch]);

  // Sortera och filtrera favoriter för hantera-favoriter-modalen (endast på namn)
  const modalFavorites = useMemo(() => {
    const base = [...favorites].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });

    if (!favoritesSearch.trim()) return base;
    const search = favoritesSearch.toLowerCase();
    return base.filter(fav => fav.name.toLowerCase().includes(search));
  }, [favorites, favoritesSearch]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center"><ShoppingCart className="h-6 w-6 text-primary mr-3 shrink-0" />{t('shoppingList.title')}</h1>
        <p className="text-muted-foreground mt-1">Planera och prioritera beredskapsinköp, samt skicka inköp till beredskapsförrådet.</p>
      </div>

      {/* Länk tillbaka till Beredskapsförråd */}
      <div className="space-y-2">
        <Link
          to="/app/inventory"
          className="flex items-center justify-between w-full p-4 rounded-xl bg-orange-400/10 border border-orange-400/30 hover:bg-orange-400/20 transition-all"
        >
          <div className="flex items-center gap-3">
            <ChevronLeft className="h-6 w-6 text-orange-400" />
            <p className="font-semibold text-orange-400">Beredskapsförråd</p>
          </div>
          <Package className="h-5 w-5 text-orange-400" />
        </Link>
        
        <Link
          to="/app/recipes"
          className="flex items-center justify-between w-full p-4 rounded-xl border transition-all"
          style={{ 
            backgroundColor: 'rgba(109, 124, 72, 0.10)', 
            borderColor: 'rgba(109, 124, 72, 0.30)' 
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.20)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.10)';
          }}
        >
          <div className="flex items-center gap-3">
            <ChefHat className="h-6 w-6" style={{ color: '#83a359' }} />
            <p className="font-semibold" style={{ color: '#83a359' }}>{t('nav.recipes')}</p>
          </div>
          <ChevronRight className="h-5 w-5" style={{ color: '#83a359' }} />
        </Link>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{t('shoppingList.yourListsTitle')}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                openModal(() => setShowFavorites(true));
              }}
              className="flex items-center gap-2 px-3 py-1 rounded text-white"
              style={{ backgroundColor: '#67724e', minHeight: '32px' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
              title="Favoriter"
            >
              <Heart className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                // Check limits: Free tier = 3 lists, Premium = 100 lists
                if (!hasPremiumAccess && lists.length >= FREE_TIER_LIMIT) {
                  setPremiumFeature('Fler än 3 inköpslistor');
                  setShowPremiumModal(true);
                  return;
                }
                
                if (hasPremiumAccess && lists.length >= PREMIUM_LIMIT) {
                  // Premium users can't create more than 100 lists
                  return;
                }
                
                setEditingList(null);
                setListForm({ name: '', description: '', store: '', isPrimary: false });
                openModal(() => setShowListForm(true));
              }}
              className="flex items-center gap-2 px-3 py-1 rounded text-primary-foreground"
              style={{ backgroundColor: '#67724e' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
            >
              <Plus className="h-4 w-4" />
              {t('shoppingList.create')}
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mb-4 bg-muted rounded-lg px-3 py-2">
          <span className="text-sm text-muted-foreground">Ordna:</span>
          <div className="flex items-center gap-1 bg-background rounded-lg p-1">
            <button
              onClick={() => setSortMode('location')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                sortMode === 'location' ? 'bg-primary/20 text-primary font-medium shadow-sm' : 'hover:bg-muted/50'
              }`}
            >
              Butik & Hylla
            </button>
            <button
              onClick={() => setSortMode('name')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                sortMode === 'name' ? 'bg-primary/20 text-primary font-medium shadow-sm' : 'hover:bg-muted/50'
              }`}
            >
              Artikelnamn
            </button>
          </div>
        </div>

        {lists.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-border p-6 text-center">
            <p className="text-muted-foreground">{t('shoppingList.emptyDesc')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {[...lists].sort((a, b) => {
              // Primär lista alltid överst
              if (a.isPrimary && !b.isPrimary) return -1;
              if (!a.isPrimary && b.isPrimary) return 1;
              // Annars alfabetisk ordning
              return a.name.localeCompare(b.name, 'sv');
            }).map(list => {
              let listItems = items.filter(item => item.listId === list.id);
              
              // Sortera artiklar
              if (sortMode === 'name') {
                listItems = [...listItems].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
              } else if (sortMode === 'location') {
                listItems = [...listItems].sort((a, b) => {
                  // Sortera först efter butik, sedan hylla, sedan namn
                  const storeA = a.store || '￿';
                  const storeB = b.store || '￿';
                  if (storeA !== storeB) return storeA.localeCompare(storeB, 'sv');
                  
                  const aisleA = a.aisle || '￿';
                  const aisleB = b.aisle || '￿';
                  if (aisleA !== aisleB) return aisleA.localeCompare(aisleB, 'sv');
                  
                  return a.name.localeCompare(b.name, 'sv');
                });
              }
              
              const isExpanded = expandedLists.has(list.id);
              const purchasedCount = listItems.filter(i => i.purchased).length;
              const totalCost = listItems.reduce((sum, i) => {
                const qty = i.quantity || 1;
                const unitPrice = i.price || 0;
                return sum + unitPrice * qty;
              }, 0);
              const remainingCost = listItems.reduce((sum, i) => {
                if (i.purchased) return sum;
                const qty = i.quantity || 1;
                const unitPrice = i.price || 0;
                return sum + unitPrice * qty;
              }, 0);
              
              return (
                <div key={list.id} id={`shopping-list-${list.id}`} className="rounded-lg border bg-card overflow-hidden">
                  <div className="p-3 relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleList(list.id); }}
                        className="w-full text-left flex items-center gap-3"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        )}
                        <div className="flex-1 flex flex-col">
                          <h3 className="text-xl font-semibold mb-1 soft-wrap">
                            {list.name}
                            {list.isPrimary && (
                              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500 inline-block ml-2" />
                            )}
                          </h3>
                          {list.description && (
                            <div className="text-sm text-muted-foreground mb-1 soft-wrap">{list.description}</div>
                          )}
                          <div className="text-xs text-muted-foreground">{purchasedCount}/{listItems.length} {t('shoppingList.items.purchased')}</div>
                          {totalCost > 0 && (
                            <div className="text-xs text-muted-foreground">
                              <span className="font-semibold">
                                Total kostnad: {totalCost.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr
                              </span>
                              {remainingCost > 0 && remainingCost < totalCost && (
                                <span className="ml-1">
                                  ({remainingCost.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr kvar)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openModal(() => setFullscreenListId(list.id)); }}
                        className="absolute top-3 right-3 p-1.5 rounded hover:bg-muted"
                        title="Visa i helskärm"
                        aria-label="Visa lista i helskärm"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>

                      <div className="mt-3 flex items-center gap-2">
                        {!list.isPrimary && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(list.id); }}
                            className="p-1 rounded hover:bg-muted"
                          >
                            <Star className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setCurrentListId(list.id); setEditingItem(null); setItemForm({ name: '', category: 'mat', notes: '', link: '', quantity: 1, price: '0', store: list.store || '', aisle: '', purchased: false }); openModal(() => openItemForm()); }}
                          className="p-1 rounded hover:bg-muted"
                          title={t('shoppingList.items.add')}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setShowQuickAddFavorite(showQuickAddFavorite === list.id ? null : list.id);
                              setQuickAddSearch('');
                            }}
                            className="p-1 rounded hover:bg-muted relative quick-add-fav-button"
                            title="Lägg till favorit snabbt"
                            data-list-id={list.id}
                          >
                            <div className="relative h-4 w-4">
                              <Heart className="h-4 w-4 absolute" />
                              <Plus className="h-2 w-2 absolute bottom-0 right-0 bg-background rounded-full" strokeWidth={3} />
                            </div>
                          </button>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); openModal(() => setConfirmClearListId(list.id)); }}
                          className="p-1 rounded hover:bg-muted"
                          title="Rensa avprickade"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingList({ id: list.id, name: list.name, description: list.description, store: list.store, isPrimary: list.isPrimary }); setListForm({ name: list.name, description: list.description || '', store: list.store || '', isPrimary: list.isPrimary || false }); openModal(() => setShowListForm(true)); }}
                          className="p-1 rounded hover:bg-muted"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openModal(() => setConfirmDeleteListId(list.id)); }}
                          className="p-1 rounded hover:bg-red-500/10 text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                  {isExpanded && (
                    <div className="border-t border-border bg-muted/20">
                      {listItems.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          {t('shoppingList.items.emptyDesc')}
                        </div>
                      ) : (
                        <div className="divide-y divide-border">
                          {listItems.map(item => (
                            <div
                              key={item.id}
                              className={`p-3 flex flex-col gap-2 ${item.purchased ? 'opacity-60' : ''} pl-8 md:pl-12`}
                            >
                              <div className="flex items-start gap-3">
                                <label className="flex items-center mt-0.5">
                                  <input
                                    type="checkbox"
                                    checked={item.purchased}
                                    onChange={() => togglePurchased(item.id)}
                                    className="w-5 h-5 rounded border-2 border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
                                  />
                                </label>
                                <div className="flex-1 min-w-0">
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => togglePurchased(item.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePurchased(item.id); } }}
                                    className={`font-medium cursor-pointer select-none soft-wrap ${item.purchased ? 'line-through' : ''}`}
                                  >
                                    {item.name} {item.quantity > 1 && <span className="text-sm text-muted-foreground">(×{item.quantity})</span>}
                                  </div>
                                </div>
                              </div>

                              {(item.store || item.aisle || (item.price && item.price > 0)) && (
                                <div className="text-xs text-muted-foreground flex items-center justify-between">
                                  <span>
                                    {item.store && <span>{item.store}</span>}
                                    {item.store && item.aisle && <span> &bull; </span>}
                                    {item.aisle && <span>{item.aisle}</span>}
                                  </span>
                                  {item.price && item.price > 0 ? (
                                    (() => {
                                      const qty = item.quantity || 1;
                                      const unitPrice = item.price || 0;
                                      const totalPrice = unitPrice * qty;
                                      if (qty > 1) {
                                        return (
                                          <span className="text-right">
                                            <span className="font-medium">
                                              {totalPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr
                                            </span>
                                            <span className="ml-1">
                                              ({unitPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr/st)
                                            </span>
                                          </span>
                                        );
                                      }
                                      return (
                                        <span className="font-medium">
                                          {unitPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr
                                        </span>
                                      );
                                    })()
                                  ) : null}
                                </div>
                              )}
                              {item.notes && (
                                <div className="text-sm text-muted-foreground whitespace-pre-wrap soft-wrap">{item.notes}</div>
                              )}
                              {item.link && (
                                  <a
                                    href={item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline flex items-center gap-1"
                                    onClick={(e) => { e.stopPropagation(); }}
                                    onKeyDown={(e) => { e.stopPropagation(); }}
                                  >
                                  <ExternalLink className="h-3 w-3" />
                                  Länk
                                </a>
                              )}

                                <div className="mt-2 flex items-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setItemForInventory(item); setInventoryForm({ name: item.name, category: (item.category as InventoryCategory) || 'mat', quantity: item.quantity || 1, unit: 'st', expirationDate: '', location: 'Hemma', notes: item.notes || '', link: item.link || '' }); openModal(() => setShowAddToInventory(true)); }}
                                  className="p-1 rounded hover:bg-muted"
                                  title="Lägg till i förråd"
                                >
                                  <Package className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setItemToMove(item); const otherLists = lists.filter(l => l.id !== list.id); setTargetListId(otherLists[0]?.id || ''); openModal(() => setShowMoveItemModal(true)); }}
                                  className="p-1 rounded hover:bg-muted"
                                  title="Flytta till annan lista"
                                >
                                  <MoveRight className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    const alreadyFavorite = favorites.some(fav => 
                                      fav.name.toLowerCase() === item.name.toLowerCase()
                                    );
                                    if (alreadyFavorite) return;
                                    
                                    setEditingFavorite(null);
                                    setFavoriteForm({
                                      name: item.name,
                                      category: (item.category as InventoryCategory) || 'mat',
                                      notes: item.notes || '',
                                      link: item.link || '',
                                      store: item.store || '',
                                      aisle: item.aisle || '',
                                      price: item.price != null && item.price > 0 ? (item.price % 1 === 0 ? String(item.price) : String(item.price).replace('.', ',')) : '0',
                                    });
                                    openModal(() => setShowFavoriteForm(true));
                                  }}
                                  disabled={favorites.some(fav => fav.name.toLowerCase() === item.name.toLowerCase())}
                                  className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                  title={favorites.some(fav => fav.name.toLowerCase() === item.name.toLowerCase()) ? "Finns redan som favorit" : "Lägg till som favorit"}
                                >
                                  <Heart className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setCurrentListId(list.id); setEditingItem(item); setItemForm({ name: item.name, category: (item.category as InventoryCategory) || 'mat', notes: item.notes || '', link: item.link || '', quantity: item.quantity || 1, price: item.price != null && item.price > 0 ? (item.price % 1 === 0 ? String(item.price) : String(item.price).replace('.', ',')) : '0', store: item.store || '', aisle: item.aisle || '', purchased: item.purchased }); openItemForm(); }}
                                  className="p-1 rounded hover:bg-muted"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openModal(() => setConfirmDeleteItemId(item.id)); }}
                                  className="p-1 rounded hover:bg-red-500/10 text-red-400"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Lägg till-knapp längst ner */}
                      {isExpanded && (
                        <div className="p-3 border-t border-border">
                          <button
                            onClick={() => {
                              setCurrentListId(list.id);
                              setEditingItem(null);
                              setItemForm({ name: '', category: 'mat', notes: '', link: '', quantity: 1, price: '0', store: list.store || '', aisle: '', purchased: false });
                              openItemForm();
                            }}
                            className="w-full md:w-auto md:max-w-xs flex items-center md:justify-start justify-center gap-2 px-4 py-2 rounded-lg text-primary-foreground transition-colors md:ml-12"
                            style={{ backgroundColor: '#67724e' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                          >
                            <Plus className="h-4 w-4" />
                            Lägg till artikel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Fullscreen modal for focused shopping */}
            {(() => {
              if (!fullscreenListId) return null;
              const list = lists.find(l => l.id === fullscreenListId);
              if (!list) return null;
              const listItems = items.filter(item => item.listId === list.id).sort((a,b) => a.name.localeCompare(b.name, 'sv'));
              return (
                  <ModalBackdrop
                    onClick={() => { setFullscreenListId(null); }}
                    className="items-start overflow-y-auto"
                  >
                  <div className="w-full max-w-4xl h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] mt-8 mb-0 translate-y-6 bg-card border border-border rounded-xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    {/* Fixed header */}
                    <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
                      <h2 className="text-xl font-semibold soft-wrap">{list.name}</h2>
                      <button
                        onClick={() => { setFullscreenListId(null); }}
                        className="p-2 rounded hover:bg-muted"
                        aria-label="Stäng helskärm"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-2">
                        {(() => {
                          const sorted = [...listItems].sort((a, b) => {
                            // Items with no store should come last
                            const aHasStore = !!(a.store && a.store.trim());
                            const bHasStore = !!(b.store && b.store.trim());
                            if (aHasStore && !bHasStore) return -1;
                            if (!aHasStore && bHasStore) return 1;

                            const sa = (a.store || '').toLowerCase();
                            const sb = (b.store || '').toLowerCase();
                            if (sa < sb) return -1;
                            if (sa > sb) return 1;

                            const aa = (a.aisle || '').toLowerCase();
                            const ab = (b.aisle || '').toLowerCase();
                            if (aa < ab) return -1;
                            if (aa > ab) return 1;

                            return a.name.localeCompare(b.name, 'sv');
                          });

                          return sorted.map(item => {
                            const meta = [item.store, item.aisle].filter(Boolean).join(' • ');
                            const hasNotes = !!(item.notes && item.notes.trim());
                            const hasLink = !!(item.link && item.link.trim());
                            const isNotesExpanded = expandedNotesInFullscreen.has(item.id);
                            
                            return (
                              <div key={item.id} className="space-y-1">
                                <div
                                  className={`flex items-center justify-between p-2 rounded-lg border border-border bg-background ${item.purchased ? 'opacity-60' : ''}`}
                                >
                                  <div 
                                    className="flex items-center gap-3 flex-1 cursor-pointer"
                                    onClick={async () => { await togglePurchased(item.id); }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={item.purchased}
                                      onChange={async () => { await togglePurchased(item.id); }}
                                      className="h-4 w-4"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <div className="flex-1">
                                      <div className={`font-medium soft-wrap ${item.purchased ? 'line-through' : ''}`}>
                                        {item.name} {item.quantity > 1 && <span className="text-sm text-muted-foreground">(×{item.quantity})</span>}
                                      </div>
                                      {(meta || (item.price && item.price > 0)) && (
                                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                                          {meta && <span>{meta}</span>}
                                          {item.price && item.price > 0 ? (
                                            (() => {
                                              const qty = item.quantity || 1;
                                              const unitPrice = item.price || 0;
                                              const totalPrice = unitPrice * qty;
                                              if (qty > 1) {
                                                return (
                                                  <span className="text-right">
                                                    <span className="font-medium">
                                                      {totalPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr
                                                    </span>
                                                    <span className="ml-1">
                                                      ({unitPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr/st)
                                                    </span>
                                                  </span>
                                                );
                                              }
                                              return (
                                                <span className="font-medium">
                                                  {unitPrice.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr
                                                </span>
                                              );
                                            })()
                                          ) : null}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-1">
                                    {hasLink && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(item.link, '_blank', 'noopener,noreferrer');
                                        }}
                                        className="p-1.5 rounded hover:bg-muted transition-colors flex-shrink-0"
                                        title="Öppna länk"
                                      >
                                        <ExternalLink className="h-4 w-4 text-green-500" />
                                      </button>
                                    )}
                                    
                                    {hasNotes && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedNotesInFullscreen(prev => {
                                            const next = new Set(prev);
                                            if (next.has(item.id)) {
                                              next.delete(item.id);
                                            } else {
                                              next.add(item.id);
                                            }
                                            return next;
                                          });
                                        }}
                                        className="p-1.5 rounded hover:bg-muted transition-colors flex-shrink-0"
                                        title={isNotesExpanded ? 'Dölj anteckningar' : 'Visa anteckningar'}
                                      >
                                        <Info className="h-4 w-4 text-blue-500" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                {hasNotes && isNotesExpanded && (
                                  <div className="ml-10 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground border border-border whitespace-pre-wrap soft-wrap">
                                    {item.notes}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                  </ModalBackdrop>
              );
            })()}
          </div>
        )}
      </div>

      {/* List Form Modal */}
      {showListForm && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingList ? t('shoppingList.edit') : 'Ny inköpslista'}
              </h3>
              <button onClick={() => { setShowListForm(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('shoppingList.name')}</label>
                <input
                  type="text"
                  value={listForm.name}
                  maxLength={25}
                  onChange={(e) => setListForm(prev => ({ ...prev, name: e.target.value.slice(0, 25) }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder={t('shoppingList.name')}
                />
                <div className="text-xs text-muted-foreground mt-1">{listForm.name.length}/25</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('shoppingList.description')}</label>
                <textarea
                  value={listForm.description}
                  maxLength={200}
                  onChange={(e) => setListForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder={t('shoppingList.description')}
                />
                <div className="text-xs text-muted-foreground mt-1">{(listForm.description || '').length}/200</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Butik (valfritt)</label>
                <input
                  type="text"
                  value={listForm.store}
                  maxLength={50}
                  onChange={(e) => setListForm(prev => ({ ...prev, store: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="T.ex. ICA Maxi"
                />
                <div className="text-xs text-muted-foreground mt-1">{(listForm.store || '').length}/50</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Butiken förifylls automatiskt på nya artiklar
                </p>
              </div>
              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isPrimary || false}
                    onChange={(e) => setListForm(prev => ({ ...prev, isPrimary: e.target.checked }))}
                    className="w-5 h-5 rounded border-2 border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Primär lista</span>
                </label>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { setShowListForm(false); }}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    const trimmed = trimmedListName;
                    if (!trimmed) return alert(t('shoppingList.nameRequired'));
                    if (!editingList && !hasMinimumLettersForNewList) return;
                    if (trimmed.length > 25) return alert('Namnet får vara max 25 tecken långt.');
                    if (editingList) {
                      await updateList(editingList.id, { 
                        name: listForm.name, 
                        description: listForm.description,
                        store: listForm.store,
                        isPrimary: listForm.isPrimary,
                      });
                      // If user made this list primary, open it and close others
                      if (listForm.isPrimary) {
                        setExpandedLists(new Set([editingList.id]));
                      }
                    } else {
                      const newList = await createList({ 
                        name: listForm.name, 
                        description: listForm.description,
                        store: listForm.store,
                        isPrimary: listForm.isPrimary,
                      });
                      // Öppna den nya listan och stäng andra
                      if (newList) {
                        setExpandedLists(new Set([newList.id]));
                      }
                    }
                    setShowListForm(false);
                  }}
                  disabled={isListSaveDisabled}
                  className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#67724e' }}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingItem ? t('shoppingList.items.edit') : t('shoppingList.items.add')}
              </h3>
                <button onClick={() => { setShowItemForm(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('shoppingList.items.name')}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemForm.name}
                    maxLength={50}
                    onChange={(e) => setItemForm(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder={t('shoppingList.items.name')}
                  />
                  <button
                    onClick={() => {
                      setSuggestionSearch('');
                      openModal(() => setShowSuggestions(true));
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa förslag"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{itemForm.name.length}/50</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={itemForm.category}
                  onChange={(e) => setItemForm(prev => ({ ...prev, category: e.target.value as InventoryCategory | '' }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="">Ingen kategori</option>
                  <option value="mat">{CATEGORY_LABELS.mat}</option>
                  <option value="medicin">{CATEGORY_LABELS.medicin}</option>
                  <option value="hygien">{CATEGORY_LABELS.hygien}</option>
                  <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
                  <option value="kommunikation">{CATEGORY_LABELS.kommunikation}</option>
                  <option value="varme">{CATEGORY_LABELS.varme}</option>
                  <option value="ljus">{CATEGORY_LABELS.ljus}</option>
                  <option value="dokument">{CATEGORY_LABELS.dokument}</option>
                  <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('shoppingList.items.notes')}</label>
                <textarea
                  value={itemForm.notes}
                  maxLength={200}
                  onChange={(e) => setItemForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder={t('shoppingList.items.notes')}
                />
                <div className="text-xs text-muted-foreground mt-1">{(itemForm.notes || '').length}/200</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('shoppingList.items.link')}</label>
                <input
                  type="url"
                  value={itemForm.link}
                  maxLength={200}
                  onChange={(e) => setItemForm(prev => ({ ...prev, link: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="https://..."
                />
                <div className="text-xs text-muted-foreground mt-1">{(itemForm.link || '').length}/200</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Antal</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setItemForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                    disabled={itemForm.quantity <= 1}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={itemForm.quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setItemForm(prev => ({ ...prev, quantity: Math.min(1000, Math.max(1, parseInt(val) || 1)) }));
                    }}
                    className="w-20 px-3 py-2 text-center rounded-lg border border-border bg-background"
                  />
                  <button
                    type="button"
                    onClick={() => setItemForm(prev => ({ ...prev, quantity: Math.min(1000, prev.quantity + 1) }))}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pris (kr)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setItemForm(prev => { const cur = parseFloat((prev.price || '0').replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    disabled={parseFloat((itemForm.price || '0').replace(',', '.')) <= 0}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={itemForm.price}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                        const parts = raw.split(',');
                        const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                        if (cleaned === '' || cleaned === ',') { setItemForm(prev => ({ ...prev, price: '0' })); return; }
                        const numVal = parseFloat(cleaned.replace(',', '.'));
                        if (!isNaN(numVal) && numVal <= 1000000) setItemForm(prev => ({ ...prev, price: cleaned }));
                      }}
                      className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                    />
                    <span className="text-sm text-muted-foreground">kr</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setItemForm(prev => { const cur = parseFloat((prev.price || '0').replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Butik</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemForm.store}
                    maxLength={50}
                    onChange={(e) => setItemForm(prev => ({ ...prev, store: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. ICA Maxi"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setStoreSuggestionSearch('');
                      openModal(() => setShowStoreSuggestions(true));
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa befintliga butiker"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{(itemForm.store || '').length}/50</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hylla/Avdelning</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemForm.aisle}
                    maxLength={50}
                    onChange={(e) => setItemForm(prev => ({ ...prev, aisle: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. Mejeri"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setAisleSuggestionSearch('');
                      openModal(() => setShowAisleSuggestions(true));
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa avdelningar"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{(itemForm.aisle || '').length}/50</div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { setShowItemForm(false); }}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    if (!itemForm.name.trim()) return;
                    if (editingItem) {
                      await updateItem(editingItem.id, {
                        name: itemForm.name,
                        category: itemForm.category || undefined,
                        notes: itemForm.notes || undefined,
                        link: itemForm.link || undefined,
                        quantity: itemForm.quantity,
                        price: parseFloat((itemForm.price || '0').replace(',', '.')) || 0,
                        store: itemForm.store || undefined,
                        aisle: itemForm.aisle || undefined,
                        purchased: itemForm.purchased,
                      });
                    } else if (currentListId) {
                      await createItem({
                        listId: currentListId,
                        name: itemForm.name,
                        category: itemForm.category || undefined,
                        notes: itemForm.notes,
                        link: itemForm.link,
                        quantity: itemForm.quantity,
                        price: parseFloat((itemForm.price || '0').replace(',', '.')) || 0,
                        store: itemForm.store,
                        aisle: itemForm.aisle,
                        purchased: itemForm.purchased,
                      });
                      // Öppna listan där artikeln lades till och stäng andra
                      setExpandedLists(new Set([currentListId]));
                    }
                    setShowItemForm(false);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90"
                  style={{ backgroundColor: '#67724e' }}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Modal */}
      {showSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj vara från förslag</h3>
              <button onClick={() => { setShowSuggestions(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={suggestionSearch}
                  onChange={(e) => setSuggestionSearch(e.target.value)}
                  placeholder="Sök vara..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredSuggestions.map((suggestion) => (
                <button
                  key={suggestion.name}
                  onClick={() => {
                    setItemForm(prev => ({ ...prev, name: suggestion.name, aisle: suggestion.aisle }));
                    setShowSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="font-medium">{suggestion.name}</div>
                  {suggestion.aisle && (
                    <div className="text-xs text-muted-foreground">{suggestion.aisle}</div>
                  )}
                </button>
              ))}
              {filteredSuggestions.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga förslag hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Confirmation dialogs: delete list, clear purchased items, delete item */}
      {confirmDeleteListId ? (() => {
        const list = lists.find(l => l.id === confirmDeleteListId);
        return (
          <ModalBackdrop>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Bekräfta borttagning</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Är du säker på att du vill ta bort <span className="font-medium text-foreground">{list?.name}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Denna lista och alla dess artiklar kommer att tas bort permanent.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setConfirmDeleteListId(null); }}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={async () => {
                    await deleteList(confirmDeleteListId as string);
                    setConfirmDeleteListId(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </ModalBackdrop>
        );
      })() : null}

      {confirmClearListId ? (() => {
        const list = lists.find(l => l.id === confirmClearListId);
        return (
          <ModalBackdrop>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Minus className="h-6 w-6 text-yellow-500" />
                </div>
                <h2 className="text-xl font-semibold">Rensa avprickade artiklar</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Vill du ta bort alla markerade artiklar från <span className="font-medium text-foreground">{list?.name}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Detta raderar endast artiklar som redan är markerade som köpta.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setConfirmClearListId(null); }}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={async () => { await clearPurchasedForList(confirmClearListId as string); setConfirmClearListId(null); }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Rensa
                </button>
              </div>
            </div>
          </ModalBackdrop>
        );
      })() : null}

      {confirmDeleteItemId ? (() => {
        const item = items.find(i => i.id === confirmDeleteItemId);
        return (
          <ModalBackdrop>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Bekräfta borttagning</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Är du säker på att du vill ta bort <span className="font-medium text-foreground">{item?.name}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Denna artikel kommer att tas bort permanent.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setConfirmDeleteItemId(null); }}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={async () => { await deleteItem(confirmDeleteItemId as string); setConfirmDeleteItemId(null); }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </ModalBackdrop>
        );
      })() : null}

      {/* Store Suggestions Modal */}
      {showStoreSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj butik</h3>
              <button onClick={() => { setShowStoreSuggestions(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={storeSuggestionSearch}
                  onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                  placeholder="Sök butik..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredStores.map((store) => (
                <button
                  key={store}
                  onClick={() => {
                    setItemForm(prev => ({ ...prev, store }));
                    setShowStoreSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {store}
                </button>
              ))}
              {filteredStores.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga butiker hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Aisle Suggestions Modal */}
      {showAisleSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj avdelning</h3>
              <button onClick={() => { setShowAisleSuggestions(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={aisleSuggestionSearch}
                  onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                  placeholder="Sök avdelning..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredAisles.map((aisle) => (
                <button
                  key={aisle}
                  onClick={() => {
                    setItemForm(prev => ({ ...prev, aisle }));
                    setShowAisleSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {aisle}
                </button>
              ))}
              {filteredAisles.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga avdelningar hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Modal för att flytta artikel */}
      {showMoveItemModal && itemToMove && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/20">
                <MoveRight className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Flytta artikel</h2>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Artikel</p>
              <p className="font-medium">{itemToMove.name}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Flytta till lista
              </label>
              <select
                value={targetListId}
                onChange={(e) => setTargetListId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background"
              >
                {lists
                  .filter(l => l.id !== itemToMove.listId)
                  .sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return a.name.localeCompare(b.name, 'sv');
                  })
                  .map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name} {list.isPrimary ? '★' : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMoveItemModal(false);
                  setItemToMove(null);
                  setTargetListId('');
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  if (!targetListId) return;
                  
                  // Flytta artikeln genom att uppdatera listId
                  await updateItem(itemToMove.id, { listId: targetListId });
                  
                  // Stäng modal
                  setShowMoveItemModal(false);
                  setItemToMove(null);
                  setTargetListId('');
                  
                  // Hämta uppdaterad data
                  await fetchFromServer();
                }}
                  disabled={!targetListId}
                  className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Flytta
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal för att lägga till i förråd */}
      {showAddToInventory && itemForInventory && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/20">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Lägg till i förråd</h2>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Namn</label>
                <input
                  type="text"
                  value={inventoryForm.name}
                  maxLength={50}
                  onChange={(e) => setInventoryForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="Namn på föremål"
                />
                <div className="text-xs text-muted-foreground mt-1">{inventoryForm.name.length}/50</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={inventoryForm.category}
                  onChange={(e) => setInventoryForm(prev => ({ ...prev, category: e.target.value as InventoryCategory }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="mat">{CATEGORY_LABELS.mat}</option>
                  <option value="medicin">{CATEGORY_LABELS.medicin}</option>
                  <option value="hygien">{CATEGORY_LABELS.hygien}</option>
                  <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
                  <option value="varme">{CATEGORY_LABELS.varme}</option>
                  <option value="ljus">{CATEGORY_LABELS.ljus}</option>
                  <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Antal</label>
                <div className="flex items-center gap-2 max-w-xs">
                  <button
                    type="button"
                    onClick={() => setInventoryForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                    disabled={inventoryForm.quantity <= 1}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={inventoryForm.quantity}
                    onChange={(e) => setInventoryForm(prev => ({ ...prev, quantity: Math.min(1000, Math.max(1, Number(e.target.value))) }))}
                    min={1}
                    max={1000}
                    className="w-20 px-3 py-2 text-center rounded-lg border border-border bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setInventoryForm(prev => ({ ...prev, quantity: Math.min(1000, prev.quantity + 1) }))}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Enhet</label>
                <select
                  value={inventoryForm.unit}
                  onChange={(e) => setInventoryForm(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="st">Styck (st)</option>
                  <option value="kg">Kilogram (kg)</option>
                  <option value="g">Gram (g)</option>
                  <option value="l">Liter (l)</option>
                  <option value="ml">Milliliter (ml)</option>
                  <option value="förp">Förpackning (förp)</option>
                  <option value="burk">Burk</option>
                  <option value="påse">Påse</option>
                  <option value="flaska">Flaska</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Utgångsdatum (valfritt)</label>
                <input
                  type="date"
                  value={inventoryForm.expirationDate}
                  onChange={(e) => setInventoryForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Plats</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inventoryForm.location}
                    maxLength={50}
                    onChange={(e) => setInventoryForm(prev => ({ ...prev, location: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. Hemma, Källaren"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setInventoryLocationSearch('');
                      openModal(() => setShowInventoryLocationSuggestions(true));
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa befintliga platser"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{inventoryForm.location.length}/50</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Anteckningar</label>
                <textarea
                  value={inventoryForm.notes}
                  maxLength={200}
                  onChange={(e) => setInventoryForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="Eventuella anteckningar"
                />
                <div className="text-xs text-muted-foreground mt-1">{(inventoryForm.notes || '').length}/200</div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddToInventory(false);
                  setItemForInventory(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  if (!inventoryForm.name || inventoryForm.quantity < 1) return;
                  
                  // Bestäm familyId (om användaren är i familjeläge ska vi skicka familyId)
                  const { getEffectiveFamilyOwnerId } = await import('@/stores/family-store');
                  const ownerId = getEffectiveFamilyOwnerId();
                  const familyId = ownerId || undefined;

                  await addInventoryItem({
                    name: inventoryForm.name,
                    category: inventoryForm.category,
                    quantity: inventoryForm.quantity,
                    unit: inventoryForm.unit,
                    expirationDate: inventoryForm.expirationDate === '' ? undefined : inventoryForm.expirationDate,
                    location: inventoryForm.location,
                    notes: inventoryForm.notes === '' ? undefined : inventoryForm.notes,
                    link: inventoryForm.link === '' ? undefined : inventoryForm.link,
                  }, familyId);
                  
                  // Stäng modal
                  setShowAddToInventory(false);
                  setItemForInventory(null);
                }}
                disabled={!inventoryForm.name || inventoryForm.quantity < 1}
                className="flex-1 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#67724e' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
              >
                Lägg till
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Location Suggestions Modal */}
      {showInventoryLocationSuggestions && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj plats</h3>
              <button onClick={() => { setShowInventoryLocationSuggestions(false); }} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={inventoryLocationSearch}
                  onChange={(e) => setInventoryLocationSearch(e.target.value)}
                  placeholder="Sök plats..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredInventoryLocations.map((location) => (
                <button
                  key={location}
                  onClick={() => {
                    setInventoryForm(prev => ({ ...prev, location }));
                    setShowInventoryLocationSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {location}
                </button>
              ))}
              {filteredInventoryLocations.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga platser hittades
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Favoriter Modal */}
      {showFavorites && (
        <ModalBackdrop>
          <div className="bg-card rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Heart className="h-6 w-6 text-[#67724e]" />
                <h2 className="text-2xl font-bold">Hantera favoriter</h2>
              </div>
              <button
                onClick={() => {
                  setShowFavorites(false);
                }}
                className="p-2 rounded hover:bg-muted transition-colors"
                aria-label="Stäng"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Inga favoriter ännu</p>
                  <p className="text-sm text-muted-foreground mt-1">Klicka på "Ny favorit" för att lägga till</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="mb-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={favoritesSearch}
                        onChange={(e) => setFavoritesSearch(e.target.value)}
                        placeholder="Sök favoriter..."
                        className="w-full pl-9 pr-9 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {favoritesSearch && (
                        <button
                          type="button"
                          onClick={() => setFavoritesSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                          aria-label="Rensa sökfält"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {modalFavorites.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Inga favoriter matchar sökningen</p>
                  ) : (
                    modalFavorites.map((favorite) => (
                      <div
                        key={favorite.id}
                        className="rounded-lg border border-border bg-muted/30 p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg soft-wrap">{favorite.name}</h3>
                            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                              {favorite.category && (
                                <p>
                                  <span className="font-medium">Kategori:</span>{' '}
                                  {CATEGORY_LABELS[favorite.category as InventoryCategory] || favorite.category}
                                </p>
                              )}
                              {favorite.store && (
                                <p className="soft-wrap">
                                  <span className="font-medium">Butik:</span> {favorite.store}
                                </p>
                              )}
                              {favorite.aisle && (
                                <p className="soft-wrap">
                                  <span className="font-medium">Hylla/Avdelning:</span> {favorite.aisle}
                                </p>
                              )}
                              {favorite.notes && (
                                <p className="soft-wrap">
                                  <span className="font-medium">Anteckningar:</span> {favorite.notes}
                                </p>
                              )}
                              {favorite.link && (
                                <p className="flex items-center gap-1">
                                  <span className="font-medium">Länk:</span>
                                  <a
                                    href={favorite.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline inline-flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Öppna <ExternalLink className="h-3 w-3" />
                                  </a>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditFavorite(favorite)}
                              className="p-2 rounded hover:bg-muted transition-colors"
                              title="Redigera"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmDeleteFavoriteId(favorite.id);
                              }}
                              className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 transition-colors"
                              title="Ta bort"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border">
              <button
                onClick={() => {
                  setEditingFavorite(null);
                  setFavoriteForm({
                    name: '',
                    category: 'mat',
                    notes: '',
                    link: '',
                    store: '',
                    aisle: '',
                  });
                  openModal(() => setShowFavoriteForm(true));
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: '#67724e' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
              >
                <Plus className="h-4 w-4" />
                Ny favorit
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Favorit Formulär Modal */}
      {showFavoriteForm && (
        <ModalBackdrop>
          <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold">{editingFavorite ? 'Redigera favorit' : 'Ny favorit'}</h2>
              <button 
                onClick={() => { 
                  setShowFavoriteForm(false);
                  setEditingFavorite(null);
                  setFavoriteForm({ name: '', category: 'mat', notes: '', link: '', store: '', aisle: '', price: '0' });
                  setLinkError('');
                }} 
                className="p-2 rounded hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Namn *</label>
                <input
                  type="text"
                  value={favoriteForm.name}
                  maxLength={50}
                  onChange={(e) => setFavoriteForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="T.ex. Konserverade bönor"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  autoFocus
                />
                <div className="text-xs text-muted-foreground mt-1">{favoriteForm.name.length}/50</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={favoriteForm.category}
                  onChange={(e) => setFavoriteForm(prev => ({ ...prev, category: e.target.value as InventoryCategory }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Anteckningar</label>
                <textarea
                  value={favoriteForm.notes || ''}
                  maxLength={200}
                  onChange={(e) => setFavoriteForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="T.ex. Ekologisk version"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background resize-none"
                  rows={3}
                />
                <div className="text-xs text-muted-foreground mt-1">{(favoriteForm.notes || '').length}/200</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Länk</label>
                <input
                  type="url"
                  value={favoriteForm.link || ''}
                  maxLength={200}
                  onChange={(e) => {
                    setFavoriteForm(prev => ({ ...prev, link: e.target.value }));
                    if (linkError) setLinkError('');
                  }}
                  onBlur={(e) => {
                    const url = e.target.value;
                    if (url && !validateUrl(url)) {
                      setLinkError('Ogiltig URL. Ange en giltig webbadress (t.ex. https://example.com)');
                    }
                  }}
                  placeholder="https://example.com"
                  className={`w-full px-3 py-2 rounded-lg border ${linkError ? 'border-red-500' : 'border-border'} bg-background`}
                />
                {linkError ? (
                  <p className="text-sm text-red-500 mt-1">{linkError}</p>
                ) : (
                  <div className="text-xs text-muted-foreground mt-1">{(favoriteForm.link || '').length}/200</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Pris (kr)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFavoriteForm(prev => { const cur = parseFloat(((prev.price || '0')).replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    disabled={parseFloat(((favoriteForm.price || '0')).replace(',', '.')) <= 0}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={favoriteForm.price || '0'}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                        const parts = raw.split(',');
                        const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                        if (cleaned === '' || cleaned === ',') { setFavoriteForm(prev => ({ ...prev, price: '0' })); return; }
                        const numVal = parseFloat(cleaned.replace(',', '.'));
                        if (!isNaN(numVal) && numVal <= 1000000) setFavoriteForm(prev => ({ ...prev, price: cleaned }));
                      }}
                      className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                    />
                    <span className="text-sm text-muted-foreground">kr</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFavoriteForm(prev => { const cur = parseFloat(((prev.price || '0')).replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Butik</label>
                <input
                  type="text"
                  value={favoriteForm.store || ''}
                  maxLength={50}
                  onChange={(e) => setFavoriteForm(prev => ({ ...prev, store: e.target.value }))}
                  placeholder="T.ex. ICA Maxi"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
                <div className="text-xs text-muted-foreground mt-1">{(favoriteForm.store || '').length}/50</div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Hylla/Avdelning</label>
                <input
                  type="text"
                  value={favoriteForm.aisle || ''}
                  maxLength={50}
                  onChange={(e) => setFavoriteForm(prev => ({ ...prev, aisle: e.target.value }))}
                  placeholder="T.ex. Konserver"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
                <div className="text-xs text-muted-foreground mt-1">{(favoriteForm.aisle || '').length}/50</div>
              </div>
            </div>

            <div className="p-6 border-t border-border flex gap-3">
              <button
                onClick={() => {
                  setShowFavoriteForm(false);
                  setEditingFavorite(null);
                  setFavoriteForm({ name: '', category: 'mat', notes: '', link: '', store: '', aisle: '', price: '0' });
                  setLinkError('');
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={editingFavorite ? handleUpdateFavorite : handleCreateFavorite}
                disabled={!favoriteForm.name.trim()}
                className="flex-1 px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#67724e' }}
                onMouseEnter={(e) => {
                  if (!favoriteForm.name.trim()) return;
                  ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f');
                }}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
              >
                {editingFavorite ? 'Spara' : 'Skapa'}
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Bekräfta borttagning av favorit */}
      {confirmDeleteFavoriteId && (
        <ModalBackdrop>
          <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Ta bort favorit?</h2>
            <p className="text-muted-foreground mb-6">
              Är du säker på att du vill ta bort denna favorit? Detta går inte att ångra.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setConfirmDeleteFavoriteId(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => handleDeleteFavorite(confirmDeleteFavoriteId)}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Ta bort
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}
      
      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature={premiumFeature}
      />

      {/* Quick Add Favorite Dropdown - Portal-style rendering */}
      {showQuickAddFavorite && dropdownPosition && (
        <div 
          className="quick-add-dropdown fixed w-64 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col"
          style={{ 
            top: `${dropdownPosition.top}px`, 
            left: `${dropdownPosition.left}px`,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={quickAddSearch}
                onChange={(e) => setQuickAddSearch(e.target.value)}
                placeholder="Sök favoriter..."
                className="w-full pl-8 pr-3 py-1.5 bg-background border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredFavorites.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {quickAddSearch ? 'Inga favoriter matchar sökningen' : 'Inga favoriter sparade'}
              </div>
            ) : (
              filteredFavorites.map((fav) => {
                const alreadyInList = showQuickAddFavorite ? items.some(item => 
                  item.listId === showQuickAddFavorite && 
                  item.name.toLowerCase() === fav.name.toLowerCase()
                ) : false;
                
                return (
                  <button
                    key={fav.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAddFavorite(showQuickAddFavorite, fav);
                    }}
                    disabled={addedFavoriteId === fav.id}
                    className={`w-full text-left px-3 py-2 transition-colors border-b border-border last:border-b-0 relative ${
                      addedFavoriteId === fav.id 
                        ? 'bg-green-500/20 cursor-default' 
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{fav.name}</div>
                        {(fav.store || fav.aisle) && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {[fav.store, fav.aisle].filter(Boolean).join(' • ')}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {addedFavoriteId === fav.id ? (
                          <Check className="h-5 w-5 text-green-500 animate-in fade-in zoom-in duration-300" />
                        ) : alreadyInList ? (
                          <Check className="h-5 w-5 text-orange-500/80" />
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
